import { z } from 'zod';

/**
 * Redes sociais (docs/redes-sociais, decidido pelo GrowuP a 22-10-2026): o
 * planeamento dos conteudos para as redes da Grow uP. Esta parte e o banco de
 * ideias; os planos e os passos (Filmagem, Edicao, Aprovacao, Publicacao) vem
 * a seguir.
 *
 *   - `/api/site/social/ideas/...`  back-office, `social.manage` (Gestor de Redes
 *     Sociais) ou `social.create` (Influencer)
 */

export const SOCIAL_FORMATS = [
  'vertical',
  'short',
  'long_video',
  'photos',
  'clip',
  'other',
] as const;
export const socialFormatSchema = z.enum(SOCIAL_FORMATS);
export type SocialFormat = z.infer<typeof socialFormatSchema>;

export const SOCIAL_FORMAT_LABELS: Record<SocialFormat, string> = {
  vertical: 'Reel / TikTok (vertical)',
  short: 'YouTube Short',
  long_video: 'Vídeo longo (YouTube)',
  photos: 'Fotos (carrossel)',
  clip: 'Clip Twitch',
  other: 'Outro',
};

/** As redes da Grow uP, por esta ordem. */
export const SOCIAL_NETWORKS = [
  'instagram',
  'tiktok',
  'youtube',
  'x',
  'facebook',
  'twitch',
] as const;
export const socialNetworkSchema = z.enum(SOCIAL_NETWORKS);
export type SocialNetwork = z.infer<typeof socialNetworkSchema>;

export const SOCIAL_NETWORK_LABELS: Record<SocialNetwork, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  facebook: 'Facebook',
  twitch: 'Twitch',
};

/** `suggested`: sugerida por um influencer, por aceitar; `accepted`: no banco. */
export const socialIdeaStatusSchema = z.enum(['suggested', 'accepted', 'rejected']);
export type SocialIdeaStatus = z.infer<typeof socialIdeaStatusSchema>;

export const SOCIAL_IDEA_STATUS_LABELS: Record<SocialIdeaStatus, string> = {
  suggested: 'Por aceitar',
  accepted: 'No banco',
  rejected: 'Recusada',
};

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .regex(/^https:\/\/\S+$/, 'use um link https://');

const tagSchema = z.string().trim().toLowerCase().min(1).max(30, 'etiquetas até 30 letras');

/** Sem repetidos, pela ordem em que vieram. */
const unique = <T>(list: T[]) => [...new Set(list)];

export const socialIdeaInputSchema = z.object({
  title: z.string().trim().min(1, 'escreva a ideia').max(140),
  description: z.string().trim().max(4000).default(''),
  format: socialFormatSchema,
  networks: z.array(socialNetworkSchema).max(SOCIAL_NETWORKS.length).default([]).transform(unique),
  durationSeconds: z.number().int().min(1).max(36_000).nullable().default(null),
  referenceUrls: z.array(httpsUrl).max(10, 'até 10 links').default([]).transform(unique),
  tags: z.array(tagSchema).max(20, 'até 20 etiquetas').default([]).transform(unique),
});
export type SocialIdeaInput = z.input<typeof socialIdeaInputSchema>;

export const socialIdeaReviewInputSchema = z.object({
  accept: z.boolean(),
  note: z.string().trim().max(1000).default(''),
});
export type SocialIdeaReviewInput = z.input<typeof socialIdeaReviewInputSchema>;

export const socialIdeaSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  format: socialFormatSchema,
  networks: z.array(socialNetworkSchema),
  durationSeconds: z.number().int().nullable(),
  referenceUrls: z.array(z.string()),
  tags: z.array(z.string()),
  status: socialIdeaStatusSchema,
  reviewNote: z.string(),
  /** Login de quem aceitou ou recusou. */
  reviewedBy: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  /** Login de quem a criou (ou sugeriu) e de quem mexeu por ultimo. */
  createdBy: z.string(),
  createdAt: z.string(),
  updatedBy: z.string(),
  updatedAt: z.string(),
  /** Quem pede pode muda-la ou apaga-la. */
  canEdit: z.boolean(),
});
export type SocialIdea = z.infer<typeof socialIdeaSchema>;

/** `75` -> `1:15`. */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

/** `1:15`, `75` ou `75s` -> segundos; `null` se nao se perceber. */
export function parseDuration(text: string): number | null {
  const t = text.trim();
  const clock = /^(\d{1,3}):([0-5]\d)$/.exec(t);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  const plain = /^(\d{1,5})\s*s?$/.exec(t);
  if (plain) return Number(plain[1]);
  return null;
}

// ---- Planos e pecas (parte 2) --------------------------------------------------------
//
//   - `/api/site/social/plans/...`  o gestor ve todos; um influencer so os seus

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'aaaa-mm-dd');

/** Os passos de cada peca, por esta ordem. */
export const SOCIAL_STEPS = ['filming', 'editing', 'approval', 'publishing'] as const;
export const socialStepSchema = z.enum(SOCIAL_STEPS);
export type SocialStep = z.infer<typeof socialStepSchema>;

export const SOCIAL_STEP_LABELS: Record<SocialStep, string> = {
  filming: 'Filmagem',
  editing: 'Edição',
  approval: 'Aprovação',
  publishing: 'Publicação',
};

export const socialPrioritySchema = z.enum(['must', 'nice']);
export type SocialPriority = z.infer<typeof socialPrioritySchema>;

export const SOCIAL_PRIORITY_LABELS: Record<SocialPriority, string> = {
  must: 'Obrigatória',
  nice: 'Se houver tempo',
};

/** O estado de uma peca, calculado pelos passos (RN-RS3). */
export const socialPieceStatusSchema = z.enum([
  'to_film',
  'to_edit',
  'to_approve',
  'to_publish',
  'published',
  'abandoned',
]);
export type SocialPieceStatus = z.infer<typeof socialPieceStatusSchema>;

export const SOCIAL_PIECE_STATUS_LABELS: Record<SocialPieceStatus, string> = {
  to_film: 'Por filmar',
  to_edit: 'Por editar',
  to_approve: 'Por aprovar',
  to_publish: 'Por publicar',
  published: 'Publicada',
  abandoned: 'Abandonada',
};

const STEP_STATUS: Record<SocialStep, SocialPieceStatus> = {
  filming: 'to_film',
  editing: 'to_edit',
  approval: 'to_approve',
  publishing: 'to_publish',
};

/** O primeiro passo por fazer (sem contar os "nao se aplica") diz o estado. */
export function pieceStatus(
  abandoned: boolean,
  steps: readonly { step: SocialStep; done: boolean; notApplicable: boolean }[],
): SocialPieceStatus {
  if (abandoned) return 'abandoned';
  for (const step of SOCIAL_STEPS) {
    const s = steps.find((x) => x.step === step);
    if (s && !s.done && !s.notApplicable) return STEP_STATUS[step];
  }
  return 'published';
}

export const socialPersonSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
});
export type SocialPerson = z.infer<typeof socialPersonSchema>;

export const socialPieceStepSchema = z.object({
  step: socialStepSchema,
  assignee: socialPersonSchema.nullable(),
  dueOn: z.string().nullable(),
  notApplicable: z.boolean(),
  linkUrl: z.string(),
  notes: z.string(),
  done: z.boolean(),
  doneAt: z.string().nullable(),
  /** Quem fez; `markedBy` e quem marcou, quando foi outra pessoa. */
  doneBy: z.string().nullable(),
  markedBy: z.string().nullable(),
  /** Aprovacao: a nota do ultimo pedido de alteracoes. */
  reviewNote: z.string(),
  /** Feito com um passo anterior por fazer (RN-RS1): aviso, nao bloqueia. */
  outOfOrder: z.boolean(),
  /** Prazo antes de hoje (Lisboa) e por fazer. */
  overdue: z.boolean(),
});
export type SocialPieceStep = z.infer<typeof socialPieceStepSchema>;

export const socialPublicationSchema = z.object({
  network: socialNetworkSchema,
  url: z.string(),
  publishedBy: z.string(),
  publishedAt: z.string(),
});
export type SocialPublication = z.infer<typeof socialPublicationSchema>;

export const socialPieceSchema = z.object({
  id: z.string().uuid(),
  /** Com evento: o dia e a zona (do Espaco) onde se filma. */
  eventDay: z.string().nullable(),
  zone: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  ideaId: z.string().uuid().nullable(),
  title: z.string(),
  description: z.string(),
  format: socialFormatSchema,
  networks: z.array(socialNetworkSchema),
  durationSeconds: z.number().int().nullable(),
  referenceUrls: z.array(z.string()),
  priority: socialPrioritySchema,
  publishOn: z.string().nullable(),
  notes: z.string(),
  status: socialPieceStatusSchema,
  abandonReason: z.string(),
  steps: z.array(socialPieceStepSchema),
  publications: z.array(socialPublicationSchema),
});
export type SocialPiece = z.infer<typeof socialPieceSchema>;

export const socialPlanSummarySchema = z.object({
  /** Pecas que contam (sem as abandonadas). */
  total: z.number().int(),
  filmed: z.number().int(),
  edited: z.number().int(),
  published: z.number().int(),
  abandoned: z.number().int(),
  /** Obrigatorias ainda por publicar. */
  mustPending: z.number().int(),
  /** Passos com o prazo passado. */
  overdue: z.number().int(),
});
export type SocialPlanSummary = z.infer<typeof socialPlanSummarySchema>;

export const socialPlanEventSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  /** Os dias do evento em Lisboa (`aaaa-mm-dd`). */
  days: z.array(z.string()),
});
export type SocialPlanEvent = z.infer<typeof socialPlanEventSchema>;

export const socialPlanListItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  event: socialPlanEventSchema.nullable(),
  goal: z.string(),
  startsOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  members: z.array(socialPersonSchema),
  summary: socialPlanSummarySchema,
});
export type SocialPlanListItem = z.infer<typeof socialPlanListItemSchema>;

export const socialPlanSchema = socialPlanListItemSchema.extend({
  pieces: z.array(socialPieceSchema),
  /** As zonas do Espaco do evento ligado (vazio sem evento). */
  zones: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** Quem pede gere o plano (`social.manage`). */
  canManage: z.boolean(),
  updatedAt: z.string(),
  updatedBy: z.string(),
});
export type SocialPlan = z.infer<typeof socialPlanSchema>;

export const socialPlanInputSchema = z
  .object({
    name: z.string().trim().min(1, 'dê um nome ao plano').max(120),
    goal: z.string().trim().max(1000).default(''),
    startsOn: dateSchema.nullable().default(null),
    endsOn: dateSchema.nullable().default(null),
    /** Sem datas, o plano fica com as do evento. */
    eventId: z.string().uuid().nullable().default(null),
  })
  .refine((p) => p.startsOn === null || p.endsOn === null || p.startsOn <= p.endsOn, {
    message: 'o fim tem de ser depois do início',
    path: ['endsOn'],
  });
export type SocialPlanInput = z.input<typeof socialPlanInputSchema>;

/** Designar um influencer pelo login ou email. */
export const socialMemberInputSchema = z.object({
  identifier: z.string().trim().min(2, 'escreva o login ou o email').max(254),
});

export const socialPieceInputSchema = socialIdeaInputSchema.omit({ tags: true }).extend({
  priority: socialPrioritySchema.default('must'),
  publishOn: dateSchema.nullable().default(null),
  notes: z.string().trim().max(2000).default(''),
  /** So num plano com evento: um dia do evento e uma zona do Espaco. */
  eventDay: dateSchema.nullable().default(null),
  zoneId: z.string().uuid().nullable().default(null),
});
export type SocialPieceInput = z.input<typeof socialPieceInputSchema>;

/** Copiar ideias do banco para o plano. */
export const socialPiecesFromIdeasInputSchema = z.object({
  ideaIds: z.array(z.string().uuid()).min(1).max(50),
});

export const socialAbandonInputSchema = z.object({
  abandoned: z.boolean(),
  reason: z.string().trim().max(500).default(''),
});

/** O que o gestor define em cada passo. */
export const socialStepPlanInputSchema = z.object({
  assigneeAccountId: z.string().uuid().nullable().default(null),
  dueOn: dateSchema.nullable().default(null),
  notApplicable: z.boolean().default(false),
});
export type SocialStepPlanInput = z.input<typeof socialStepPlanInputSchema>;

const optionalLink = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === '' || /^https:\/\/\S+$/.test(v), { message: 'use um link https://' })
  .default('');

/** Marcar Filmagem, Edicao ou (sem redes alvo) Publicacao. */
export const socialStepDoneInputSchema = z.object({
  done: z.boolean(),
  /** Quem fez, se nao fui eu: um influencer do plano. */
  doneByAccountId: z.string().uuid().nullable().default(null),
  linkUrl: optionalLink,
  notes: z.string().trim().max(2000).default(''),
});
export type SocialStepDoneInput = z.input<typeof socialStepDoneInputSchema>;

/** Aprovar o video final, ou pedir alteracoes (volta a Edicao). */
export const socialApprovalInputSchema = z.object({
  approved: z.boolean(),
  note: z.string().trim().max(1000).default(''),
});

export const socialPublicationInputSchema = z.object({
  url: httpsUrl,
  publishedByAccountId: z.string().uuid().nullable().default(null),
});
export type SocialPublicationInput = z.input<typeof socialPublicationInputSchema>;

// ---- Vistas (parte 3) ----------------------------------------------------------------
//
//   - `/api/site/social/calendar?from=&to=`  pecas pela data prevista de publicacao
//   - `/api/site/social/people?from=&to=`    o que cada um fez nesse periodo

/** Um periodo de ate 92 dias (um trimestre), `from` <= `to`. */
export const socialRangeQuerySchema = z
  .object({ from: dateSchema, to: dateSchema })
  .refine((r) => r.from <= r.to, { message: 'o fim tem de ser depois do início', path: ['to'] })
  .refine((r) => (Date.parse(r.to) - Date.parse(r.from)) / 86_400_000 <= 92, {
    message: 'até 92 dias de cada vez',
    path: ['to'],
  });
export type SocialRangeQuery = z.infer<typeof socialRangeQuerySchema>;

export const socialCalendarItemSchema = z.object({
  planId: z.string().uuid(),
  planName: z.string(),
  pieceId: z.string().uuid(),
  title: z.string(),
  format: socialFormatSchema,
  priority: socialPrioritySchema,
  publishOn: z.string(),
  status: socialPieceStatusSchema,
});
export type SocialCalendarItem = z.infer<typeof socialCalendarItemSchema>;

export const socialPersonWorkSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  filmed: z.number().int(),
  edited: z.number().int(),
  approved: z.number().int(),
  /** Links de publicacao (um por rede). */
  published: z.number().int(),
});
export type SocialPersonWork = z.infer<typeof socialPersonWorkSchema>;

export const socialNetworkCountSchema = z.object({
  network: socialNetworkSchema,
  count: z.number().int(),
});
export type SocialNetworkCount = z.infer<typeof socialNetworkCountSchema>;

export const socialPeopleReportSchema = z.object({
  people: z.array(socialPersonWorkSchema),
  networks: z.array(socialNetworkCountSchema),
});
export type SocialPeopleReport = z.infer<typeof socialPeopleReportSchema>;

/** Quem fez o que num plano, pelas pecas que ja vieram (sem as abandonadas). */
export function workByPerson(
  pieces: readonly SocialPiece[],
): { login: string; filmed: number; edited: number; approved: number; published: number }[] {
  const rows = new Map<
    string,
    { filmed: number; edited: number; approved: number; published: number }
  >();
  const row = (login: string) => {
    let r = rows.get(login);
    if (!r) {
      r = { filmed: 0, edited: 0, approved: 0, published: 0 };
      rows.set(login, r);
    }
    return r;
  };
  for (const p of pieces) {
    if (p.status === 'abandoned') continue;
    for (const s of p.steps) {
      if (!s.done || s.doneBy === null) continue;
      if (s.step === 'filming') row(s.doneBy).filmed++;
      if (s.step === 'editing') row(s.doneBy).edited++;
      if (s.step === 'approval') row(s.doneBy).approved++;
      // Publicacao sem redes alvo: marcada a mao, conta uma.
      if (s.step === 'publishing' && p.networks.length === 0) row(s.doneBy).published++;
    }
    for (const x of p.publications) row(x.publishedBy).published++;
  }
  return [...rows]
    .map(([login, r]) => ({ login, ...r }))
    .sort((a, b) => a.login.localeCompare(b.login));
}

/** Publicacoes por rede num plano, pela ordem das redes. */
export function publicationsByNetwork(pieces: readonly SocialPiece[]): SocialNetworkCount[] {
  return SOCIAL_NETWORKS.map((network) => ({
    network,
    count: pieces.reduce(
      (n, p) =>
        n +
        (p.status === 'abandoned' ? 0 : p.publications.filter((x) => x.network === network).length),
      0,
    ),
  })).filter((c) => c.count > 0);
}

// ---- Ligacao aos eventos (parte 4) ----------------------------------------------------
//
//   - `/api/site/social/events`               eventos para ligar a um plano (gestor)
//   - `/api/site/social/plans/:id/copy`        copiar um plano (gestor)
//   - `/api/site/events/:id/social-plans`      o separador Conteudo do evento

export const socialEventOptionSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
});
export type SocialEventOption = z.infer<typeof socialEventOptionSchema>;

/**
 * Copiar um plano (RF-RS6): as pecas que nao foram abandonadas, por fazer, sem
 * pessoas, com as datas a mesma distancia do inicio. Com evento, os dias vao
 * pela ordem e as zonas pelo nome.
 */
export const socialPlanCopyInputSchema = z.object({
  name: z.string().trim().min(1, 'dê um nome ao plano').max(120),
  startsOn: dateSchema.nullable().default(null),
  eventId: z.string().uuid().nullable().default(null),
});
export type SocialPlanCopyInput = z.input<typeof socialPlanCopyInputSchema>;
